import { motion } from 'framer-motion';
import { CodeSlideData, Theme, slideTypographyStyle } from '../slideTypes';
import { springs } from '../../../ui/slide-animations';

export function CodeSlide({
  data,
  theme,
  isEditing,
  onUpdate,
}: {
  data: CodeSlideData;
  theme: Theme;
  isEditing: boolean;
  onUpdate: (data: CodeSlideData) => void;
}) {
  return (
    <div className="h-full flex flex-col" style={{ padding: theme.spacing.compact }}>
      {isEditing ? (
        data.heading && (
          <h2
            className="mb-6"
            style={{
              ...slideTypographyStyle(theme, 'subheading'),
              color: theme.colors.text,
            }}
          >
            <input
              type="text"
              value={data.heading}
              onChange={(e) => onUpdate({ ...data, heading: e.target.value })}
              placeholder="Heading..."
              className="w-full bg-transparent border-0 border-b-2 border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none"
            />
          </h2>
        )
      ) : (
        data.heading && (
          <motion.h2
            className="mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springs.gentle as object}
            data-slide-level="heading"
            style={{
              ...slideTypographyStyle(theme, 'subheading'),
              color: theme.colors.text,
            }}
          >
            {data.heading}
          </motion.h2>
        )
      )}

      <div className="flex-1 rounded-xl overflow-hidden relative" style={{ backgroundColor: '#1e1e1e' }}>
        {isEditing ? (
          <div className="h-full flex flex-col">
            <div className="flex items-center gap-2 px-4 py-2 bg-gray-800">
              <select
                value={data.language}
                onChange={(e) => onUpdate({ ...data, language: e.target.value })}
                className="bg-gray-700 text-gray-200 px-2 py-1 rounded text-sm"
              >
                <option value="javascript">JavaScript</option>
                <option value="typescript">TypeScript</option>
                <option value="python">Python</option>
                <option value="html">HTML</option>
                <option value="css">CSS</option>
                <option value="json">JSON</option>
                <option value="sql">SQL</option>
                <option value="bash">Bash</option>
              </select>
            </div>
            <textarea
              value={data.code}
              onChange={(e) => onUpdate({ ...data, code: e.target.value })}
              placeholder="Enter your code..."
              className="flex-1 w-full p-4 bg-transparent text-gray-100 text-sm focus:outline-none resize-none"
              style={{ tabSize: 2, fontFamily: theme.fonts.code }}
            />
          </div>
        ) : (
          <>
            {/* Language badge pill */}
            <motion.div
              className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-xs font-medium"
              style={{ backgroundColor: theme.colors.primary + '25', color: theme.colors.primary }}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ ...(springs.bouncy as object), delay: 0.3 }}
            >
              {data.language}
            </motion.div>
            <motion.pre
              className="h-full p-6 overflow-auto"
              initial={{ opacity: 0, y: 15, filter: 'blur(4px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              transition={{ ...(springs.gentle as object), delay: 0.2 }}
            >
              <code
                className={`language-${data.language} text-gray-100 text-sm`}
                style={{ fontFamily: theme.fonts.code }}
              >
                {data.code}
              </code>
            </motion.pre>
          </>
        )}
      </div>
    </div>
  );
}
